// Seven-slot weekly participation projection shared by the Updates rail and
// the participation strip. Ownership evidence comes only from the selected
// week's entry; quiet is only an explicit track response. Snapshots without
// captured participants are unavailable - never supplemented from live data.
import { PROJECT_TRACKS } from "./projectTracks.js";
import { weeklyTrackState } from "./weeklyTrackCompleteness.js";

export const OWNERSHIP_ORDER = Object.freeze([
  { key: "delivery_owner", label: "Delivery Owner", org: "Ripple" },
  { key: "market_pm", label: "Market PM", org: "JLL" },
  { key: "pmo_pm", label: "PMO PM", org: "JLL" },
]);
export const PARTICIPATION_TOTAL = 7;

const text = (v) => (Array.isArray(v) ? v.some((x) => typeof x === "string" && x.trim()) : typeof v === "string" && v.trim().length > 0);

function ownershipEvidence(summary, key) {
  if (!summary) return false;
  if (key === "delivery_owner") return text(summary.narrative);
  if (key === "market_pm") return text(summary.accomplishments) || text(summary.upcoming);
  if (key === "pmo_pm") return text(summary.pmoNote);
  return false;
}

export function weeklyParticipation(project) {
  const participants = project?.participants;
  const available = Array.isArray(participants);
  const ownership = OWNERSHIP_ORDER.map((s) => ({ key: s.key, label: s.label, group: "ownership", defaultOrg: s.org }));
  const tracks = PROJECT_TRACKS.map((t) => ({ key: t.key, label: t.label, group: "tracks" }));
  if (!available) {
    return {
      available: false,
      slots: [...ownership, ...tracks].map((s) => ({ ...s, people: [], state: "unavailable" })),
      responded: null,
      total: PARTICIPATION_TOTAL,
    };
  }
  const slots = [
    ...ownership.map((s) => {
      const people = participants.filter((p) => !p.trackKey && p.ownershipSlot === s.key);
      const evidence = ownershipEvidence(project.summary, s.key);
      // Missing assignment is always unassigned; evidence without an owner is
      // shown in its section but never counted toward N/7.
      return { ...s, people, evidence, state: !people.length ? "unassigned" : evidence ? "reported" : "not_yet" };
    }),
    ...tracks.map((s) => {
      const people = participants.filter((p) => p.trackKey === s.key);
      const st = weeklyTrackState(project, s.key).state;
      return { ...s, people, evidence: st !== "not_yet", state: !people.length ? "unassigned" : st };
    }),
  ];
  return {
    available: true,
    slots,
    responded: slots.filter((s) => s.state === "reported" || s.state === "quiet").length,
    total: PARTICIPATION_TOTAL,
  };
}

export const PARTICIPATION_STATE_LABEL = Object.freeze({
  reported: "reported", quiet: "nothing this week", not_yet: "not yet", unassigned: "unassigned", unavailable: "not captured",
});
